/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, X, FileText } from 'lucide-react';
import { uploadDocument } from '@/lib/medical';
import { DOC_TYPE_META } from './WalletCard';

// ---------- Upload Dialog ----------
export const UploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('referto');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setTitle(''); setDocType('referto'); setDescription(''); setFile(null); };
  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Max 4MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, base64: reader.result });
    reader.readAsDataURL(f);
  };
  const submit = async () => {
    if (!title || !file) return;
    setSaving(true);
    try {
      await uploadDocument({ title, doc_type: docType, description: description || undefined, file_base64: file.base64, file_name: file.name });
      toast.success(t('walletUploadSuccess'));
      onUploaded?.(); reset(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || t('genericError')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md" data-testid="wallet-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> {t('walletUploadTitle')}</DialogTitle>
          <DialogDescription>{t('walletUploadDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="doc-title">{t('walletTitle')}</Label>
            <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('walletTitlePh')} data-testid="wallet-title-input" />
          </div>
          <div>
            <Label htmlFor="doc-type">{t('walletDocType')}</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger id="doc-type" data-testid="wallet-doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(DOC_TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="doc-desc">{t('walletDescription')}</Label>
            <Textarea id="doc-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('walletDescriptionPh')} data-testid="wallet-desc-input" />
          </div>
          <div>
            <Label>{t('walletFile')}</Label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="wallet-file-input" />
            {!file ? (
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-border p-6 text-center hover:border-primary/60 transition-colors" data-testid="wallet-file-btn">
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-semibold">{t('walletDropOrClick')}</p>
                <p className="text-xs text-muted-foreground">{t('walletFileHint')}</p>
              </button>
            ) : (
              <div className="rounded-2xl border border-border p-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0 text-sm truncate">{file.name}</div>
                <Button variant="ghost" size="icon" onClick={() => setFile(null)}><X className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={!title || !file || saving} data-testid="wallet-upload-confirm">
            {saving ? t('processing') : t('walletUpload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
